import React from "react";
import {
  Box,
  IconButton,
  Paper,
  Tooltip,
  InputAdornment,
  FormControl,
  OutlinedInput,
  Chip,
  Badge,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InfoIcon from "@mui/icons-material/Info";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link } from "react-router-dom";

import MyBreadcrumbs from "../MyBreadcrumbs";
import GlobalContext from "../../utils/GlobalContext";
import { FIND_RENTAL_BY_COMPANY_ID, DELETE_RENTAL } from "../../graphql/Rental";
import { chipColor } from "../../utils";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

function useQueryParam() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}
const RentalDetailCompany = (props) => {
  let q = useQueryParam();
  const history = q.get("history");

  const { cid } = props?.match?.params;
  const role = props?.role;
  const { setGlobalLoading, setSnack } = React.useContext(GlobalContext);
  const [rentals, setRentals] = React.useState([]);
  const [baseRentals, setBaseRentals] = React.useState([]);
  const [searched, setSearched] = React.useState("");
  const [isSearch, setIsSearch] = React.useState(false);
  const [company, setCompany] = React.useState(null);
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [id, setId] = React.useState(null);

  const columns = [
    {
      field: "id",
      headerName: "Nomor",
      width: 90,
      renderCell: (params) => `${params.api.getRowIndex(params.row.id) + 1}.`,
    },
    {
      field: "photocopy_name",
      headerName: "Jenis Mesin",
      flex: 1,
      renderCell: (params) => {
        const isDeadline =
          params?.row?.invoices?.filter((x) => x?.is_deadline)?.length ?? 0;
        return (
          <>
            {params?.row?.photocopy?.name}
            &emsp;
            <Badge color='error' invisible={!isDeadline} variant='dot' />
          </>
        );
      },
    },
    {
      field: "contract_date",
      headerName: "Tanggal Kontrak",
      flex: 1,
    },
    {
      field: "contract_periode",
      headerName: "Masa Kontrak",
      flex: 1,
      renderCell: (params) => `${params?.row?.contract_periode} Bulan`,
    },
    {
      field: "end_of_contract",
      headerName: "Tanggal Kontrak Berakhir",
      flex: 1,
    },
    {
      field: "contract_value",
      headerName: "Nilai Kontrak",
      width: 120,
      valueGetter: (params) =>
        `Rp. ${params?.row?.contract_value.toLocaleString("id-ID")}`,
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        let status = params?.row?.status;
        const totalInvoice = params?.row?.invoices?.length ?? 0;
        const totalPaidInvoice =
          params?.row?.invoices?.filter((x) => x?.status === "PAID")?.length ??
          0;

        if (totalInvoice > 0) {
          status =
            totalInvoice === totalPaidInvoice ? "PAID" : params?.row?.status;
        }

        return (
          <React.Fragment>
            <Chip
              label={status}
              variant='filled'
              color={chipColor(status)}
              size='small'
            />
          </React.Fragment>
        );
      },
    },
    {
      field: "oid",
      headerName: "Aksi",
      width: 90,
      renderCell: (params) => {
        return (
          <>
            <Tooltip title='Detail' placement='top'>
              <IconButton
                aria-label='detail'
                size='small'
                component={Link}
                to={`/admin/rental/${cid}/${params?.row?.uid}`}
                target='__blank'
              >
                <InfoIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
            {role === "ADMIN" && (
              <Tooltip title='Hapus' placement='top'>
                <IconButton
                  aria-label='detail'
                  size='small'
                  onClick={() => {
                    setId(params.row.uid);
                    setOpenConfirm(true);
                  }}
                >
                  <DeleteIcon fontSize='inherit' />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const requestSearch = (value) => {
    setSearched(value);
  };

  const clearSearch = () => {
    setSearched("");
    setIsSearch(false);
    setRentals([...baseRentals]);
  };

  const doSearch = async () => {
    setIsSearch(true);
  };

  /* FETCH DATA COMPANIES */
  const {
    loading: loadRentals,
    data: dataRentals,
    refetch,
  } = useQuery(FIND_RENTAL_BY_COMPANY_ID, {
    variables: { company_id: cid },
    fetchPolicy: "network-only",
    onError: (e) => console.log("error getting selor:", e),
  });

  React.useEffect(() => {
    if (loadRentals) {
      setGlobalLoading(true);
    }
  }, []);

  React.useEffect(() => {
    if (dataRentals) {
      setBaseRentals(dataRentals.findRentalByCompanyId);
      setRentals(dataRentals.findRentalByCompanyId);
      setCompany(dataRentals.findRentalByCompanyId[0]?.company);
      setGlobalLoading(false);
    }
  }, [dataRentals]);

  const [deleteExternalOrder] = useMutation(DELETE_RENTAL, {
    onCompleted: (d) => {
      setSnack({
        variant: "success",
        message: "Data Belanja berhasil dihapus",
        opened: true,
      });
      setGlobalLoading(false);
      setOpenConfirm(false);
    },
    onError: (e) => {
      console.log("error insert unit: ", e);
      let message = e.message || "Data Belanja gagal dihapus";

      setGlobalLoading(false);
      setSnack({
        variant: "error",
        message,
        opened: true,
      });
    },
  });

  const handleDelete = async (uid) => {
    await deleteExternalOrder({ variables: { uid } });
    setOpenConfirm(false);
    refetch();
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          bgcolor: "background.paper",
          borderRadius: 1,
          alignItems: "center",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <MyBreadcrumbs
            data={["Transaksi", "Mesin Fotokopi", company?.name ?? null]}
          />
        </Box>
        <Box
          sx={{
            p: 0.5,
            m: 0.5,
          }}
        >
          <FormControl size='small' margin='dense'>
            <OutlinedInput
              name='search'
              margin='dense'
              id='search'
              placeholder='Cari'
              onChange={(e) => requestSearch(e.target.value)}
              value={searched}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  doSearch();
                }
              }}
              endAdornment={
                <InputAdornment position='end'>
                  {isSearch && (
                    <IconButton onClick={() => clearSearch()}>
                      <ClearIcon />
                    </IconButton>
                  )}
                  {!isSearch && (
                    <IconButton onClick={() => doSearch()}>
                      <SearchIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              }
            />
          </FormControl>
        </Box>
      </Box>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <div style={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={
              history
                ? rentals?.filter((x) =>
                    ["TERMINATE", "DONE"].includes(x?.status)
                  )
                : rentals?.filter((x) =>
                    ["ONGOING", "NEW", "OFFERING"].includes(x?.status)
                  )
            }
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
          />
        </div>
      </Paper>
      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          Hapus Data Mesin Fotokopi
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Apakah anda yakin ingin menghapus data Mesin Fotokopi ini?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            color='error'
            onClick={() => handleDelete(id)}
            autoFocus
          >
            Ya
          </Button>
          <Button
            variant='contained'
            color='info'
            onClick={() => setOpenConfirm(false)}
          >
            Tidak
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default RentalDetailCompany;
